'use client'

import type React from 'react'
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/api'
import { toast } from 'sonner'
import { useAuth } from './auth-context'

interface Organization {
  id: string
  name: string
  role: string
  permissions?: string[]
  logo?: string
  description?: string
  onboarding?: { team_invites?: boolean }
  billing_type?: string
  public_email?: string
  url?: string
  company_size?: string
  industry?: string
  resdb_access?: boolean
}

interface OrgList {
  id: string
  name: string
  role: string
  permissions?: string[]
  logo?: string
}

type OrgContextType = {
  organizations: {
    data: OrgList[]
    isLoading: boolean
    isError: boolean
    refetch: () => void
  }
  currentOrg: Organization | null
  isLoading: boolean
  switchOrg: (orgId: string) => Promise<void>
  switchPopup: boolean
  setSwitchPopup: React.Dispatch<React.SetStateAction<boolean>>
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

export const useOrg = () => {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be used within OrgProvider')
  return ctx
}

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading: authLoading, loginWithToken, bootstrapData } = useAuth()
  const [switchPopup, setSwitchPopup] = useState(false)
  const queryClient = useQueryClient()

  const userId = useMemo(() => user?.user_id ?? null, [user?.user_id])
  const enableQueries = !authLoading && !!userId

  // Query Org List - uses bootstrapData as initialData for instant hydration
  const orgListQuery = useQuery({
    queryKey: ['organizations', userId],
    enabled: enableQueries,
    queryFn: async (): Promise<OrgList[]> => {
      const res = await api.get('/recruiter/org/list')
      return res.data?.data?.org_list || []
    },
    initialData: () => bootstrapData?.organizations?.list || undefined,
    initialDataUpdatedAt: bootstrapData ? Date.now() : undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes stale time since it is hydrated on bootstrap
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // Query Current Org - uses bootstrapData as initialData for instant hydration
  const currentOrgQuery = useQuery({
    queryKey: ['current-organization', userId],
    enabled: enableQueries,
    queryFn: async () => {
      const res = await api.get('/recruiter/org/info')
      return res.data?.data || null
    },
    initialData: () => bootstrapData?.current_organization || undefined,
    initialDataUpdatedAt: bootstrapData ? Date.now() : undefined,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  // Keep queries in sync with any bootstrapData changes (e.g. after login or initial boot)
  const orgList = bootstrapData?.organizations?.list
  const currentOrg = bootstrapData?.current_organization

  useEffect(() => {
    // If bootstrapData changes, manually update the query cache to keep context synchronous
    if (orgList && userId) {
      queryClient.setQueryData(['organizations', userId], orgList)
    }
  }, [orgList, userId, queryClient])

  useEffect(() => {
    if (currentOrg && userId) {
      queryClient.setQueryData(['current-organization', userId], currentOrg)
    }
  }, [currentOrg, userId, queryClient])

  const switchOrg = useCallback(
    async (orgId: string) => {
      try {
        setSwitchPopup(false)
        if (!user) throw new Error('Current user is null')

        // Calls backend switch and handles token refresh + bootstrap refresh
        await loginWithToken(orgId)
        window.location.replace('/')
      } catch (err) {
        toast.error('Failed to switch organization')
        throw err
      }
    },
    [user, loginWithToken]
  )

  const value = useMemo<OrgContextType>(
    () => ({
      organizations: {
        data: orgListQuery.data || [],
        isLoading: orgListQuery.isLoading && !bootstrapData,
        isError: orgListQuery.isError,
        refetch: orgListQuery.refetch,
      },
      currentOrg: currentOrgQuery.data || null,
      isLoading:
        (orgListQuery.isLoading || currentOrgQuery.isLoading || authLoading) && !bootstrapData,
      switchOrg,
      switchPopup,
      setSwitchPopup,
    }),
    [
      orgListQuery.data,
      orgListQuery.isLoading,
      orgListQuery.isError,
      orgListQuery.refetch,
      currentOrgQuery.data,
      currentOrgQuery.isLoading,
      authLoading,
      bootstrapData,
      switchOrg,
      switchPopup,
    ]
  )

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}
